import { compact, noop } from 'lodash';
import { useCallback } from 'react';

import { Visit } from '@dreryk/egabinet-visit-frontend-api';

import { useTranslate } from '@dreryk/react-common';
import { RouteLeavingGuard } from '@dreryk/react-components';

import { visitDetailsTranslationKeys } from '../../translations';
import { useVisitSettlementUpdateMedicalServicesCommand } from '../../visitSettlement/shared/medicalServices/medicalServices.hooks';

import { useUpdateVisitCommand } from './visitForm.hooks';
import { VisitFormController } from './visitForm.types';
import { validateTextEditorFields } from './VisitFormRouteLeavingGuard.utils';

type VisitFormRouteLeavingGuardProps = {
   readonly visitId: Visit['id'];
   readonly appointmentId: Visit['appointmentId'];
   readonly controller: VisitFormController;
};

export const VisitFormRouteLeavingGuard = (props: VisitFormRouteLeavingGuardProps): JSX.Element => {
   const translate = useTranslate();
   const { executeAsync: updateVisit } = useUpdateVisitCommand(props.visitId, {
      onUnexpectedError: noop,
   });
   const updateVisitSettlementMedicalServices = useVisitSettlementUpdateMedicalServicesCommand({
      appointmentId: props.appointmentId,
      control: props.controller.control,
      blockingOperation: true,
   });

   const visitDirtyFields = [
      props.controller.formState.dirtyFields.interview,
      props.controller.formState.dirtyFields.subjectiveAssessments,
      props.controller.formState.dirtyFields.recommendations,
      props.controller.formState.dirtyFields.procedures,
   ].some(Boolean);
   const visitSettlementDirtyField = Boolean(props.controller.formState.dirtyFields.visitSettlement);

   const handleBeforeLeave = useCallback((): boolean => {
      if (visitDirtyFields) {
         return validateTextEditorFields({
            controller: props.controller,
            translate: translate,
         });
      }

      return true;
   }, [visitDirtyFields, props.controller, translate]);

   const handleLeave = useCallback(async (): Promise<void> => {
      await Promise.all(
         compact([
            visitDirtyFields && props.controller.handleSubmitDraft(updateVisit)(),
            visitSettlementDirtyField && updateVisitSettlementMedicalServices(),
         ])
      );
   }, [
      props.controller,
      visitDirtyFields,
      updateVisit,
      updateVisitSettlementMedicalServices,
      visitSettlementDirtyField,
   ]);

   return (
      <RouteLeavingGuard
         testId={'formLeavingConfirmation'}
         title={visitDetailsTranslationKeys.leavingPrompt.title}
         message={visitDetailsTranslationKeys.leavingPrompt.failMessage}
         active={visitDirtyFields || visitSettlementDirtyField}
         onBeforeLeave={handleBeforeLeave}
         onLeave={handleLeave}
         successMessage={visitDetailsTranslationKeys.leavingPrompt.successMessage}
      />
   );
};
